import React from "react";
import { Award, Syringe, Pill, Globe2, TrendingUp, ExternalLink } from "lucide-react";

// Sources: Wikipedia Pharmaceutical Industry in India, IBEF, WHO, PIB, IPA-India (2024–2025).
const STATS = [
    {
        value: "#1",
        label: "Vaccine manufacturer globally",
        sub: "60%+ of world's vaccine volume",
        icon: Syringe,
    },
    {
        value: "20%",
        label: "of global generic drug supply",
        sub: "60,000+ generic brands, 60 therapeutic areas",
        icon: Pill,
    },
    {
        value: "40%",
        label: "of US generic demand",
        sub: "14% of all US generic imports by value",
        icon: Globe2,
    },
    {
        value: "3ʳᵈ",
        label: "largest by volume worldwide",
        sub: "11ᵗʰ by value · 500+ API producers",
        icon: Award,
    },
    {
        value: "70%",
        label: "of WHO vaccine stock",
        sub: "90% of WHO measles vaccines",
        icon: Syringe,
    },
    {
        value: "$30.5B",
        label: "pharma exports · FY24",
        sub: "+9.4% YoY growth",
        icon: TrendingUp,
    },
];

export default function WhyIndia() {
    return (
        <section
            className="bg-white py-24 md:py-32 border-b border-stone-200 relative overflow-hidden"
            data-testid="why-india-section"
        >
            {/* Subtle tri-color accent strip (Indian flag ribbon) - very faint */}
            <div className="absolute top-0 left-0 right-0 h-1 flex">
                <div className="flex-1 bg-[#FF9933]" />
                <div className="flex-1 bg-white" />
                <div className="flex-1 bg-[#138808]" />
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-16">
                    <div className="md:col-span-7">
                        <div className="overline mb-4">
                            <span className="inline-block w-8 h-px bg-stone-400 align-middle mr-3" />
                            Made in India
                        </div>
                        <h2 className="font-cabinet font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-ink">
                            The pharmacy
                            <br />
                            <span className="italic font-normal text-[#0C4A6E]">
                                of the world.
                            </span>
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-base md:text-lg text-stone-700 leading-relaxed">
                            Every molecule, vaccine vial and herbal formulation
                            Momia exports originates in the world's largest
                            generic-medicine and vaccine manufacturing ecosystem —
                            a 500+ API producer, WHO-GMP dense, FDA-most-approved
                            pharmaceutical base.
                        </p>
                    </div>
                </div>

                {/* Big stats grid */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-0 border border-stone-200">
                    {STATS.map((s, i) => (
                        <div
                            key={s.label}
                            className={`p-8 md:p-10 relative group hover:bg-[#FAF9F6] transition-colors
                                ${i % 2 === 0 ? "border-r border-stone-200" : ""}
                                ${i % 3 !== 2 ? "lg:border-r lg:border-stone-200" : "lg:border-r-0"}
                                ${i >= 2 ? "border-t border-stone-200" : ""}
                                ${i >= 3 ? "lg:border-t lg:border-stone-200" : ""}
                            `}
                            data-testid={`why-india-stat-${i}`}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <s.icon
                                    size={22}
                                    strokeWidth={1.5}
                                    className="text-[#0C4A6E]"
                                />
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">
                                    0{i + 1}
                                </span>
                            </div>
                            <div className="font-cabinet font-black text-5xl md:text-6xl leading-none tracking-tight text-ink">
                                {s.value}
                            </div>
                            <div className="mt-4 font-cabinet font-bold text-ink text-base leading-snug">
                                {s.label}
                            </div>
                            <div className="mt-1 text-xs text-stone-600 leading-relaxed">
                                {s.sub}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Source row */}
                <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs font-mono uppercase tracking-[0.18em] text-stone-500">
                    <div>
                        Sources · WHO · IBEF · PIB · IPA-India · FY24 figures
                    </div>
                    <a
                        href="https://www.ibef.org/industry/pharmaceutical-india"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-[#0C4A6E] hover:text-[#072F49] border-b border-[#0C4A6E]/30 hover:border-[#0C4A6E] pb-1 transition-colors"
                        data-testid="why-india-source-link"
                    >
                        Read the IBEF sector brief
                        <ExternalLink size={14} strokeWidth={1.5} />
                    </a>
                </div>
            </div>
        </section>
    );
}
